"use client";

import { useState } from "react";
import PhoneInput from "@/components/ui/PhoneInput";
import { phoneToE164, isValidPhone } from "@/lib/phone";

type Step = "idle" | "sending" | "sent";
type Channel = "email" | "sms";

export default function LoginForm() {
  const [channel, setChannel] = useState<Channel>("email");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [step, setStep] = useState<Step>("idle");
  const [error, setError] = useState<string | null>(null);

  const canSubmit = channel === "email" ? !!email.trim() : isValidPhone(phone);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!canSubmit) return;
    setError(null);
    setStep("sending");

    // Read lazily, at submit time, rather than via useSearchParams() at the
    // top of the component — that hook forces this whole page to bail to
    // client-side-only rendering (no server HTML at all), which is exactly
    // what hid the page's content from the Twilio A2P 10DLC campaign review.
    const redirectTo = new URLSearchParams(window.location.search).get("redirectTo") || "/dashboard";

    const body = channel === "email"
      ? { channel, email: email.trim(), redirectTo }
      : { channel, phoneE164: phoneToE164(phone), redirectTo };

    const res = await fetch("/api/auth/request-magic-link", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    });

    const data = await res.json();
    if (!res.ok) {
      setError(data.message || "Something went wrong. Please try again.");
      setStep("idle");
    } else {
      setStep("sent");
    }
  }

  return (
    <div style={{
      fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
      minHeight: "100vh",
      background: "#F9FAFB",
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      padding: "24px 16px",
    }}>
      <div style={{
        width: "100%",
        maxWidth: 420,
      }}>
        {/* Logo */}
        <div style={{ textAlign: "center", marginBottom: 40 }}>
          <img src="/rentsgo-logo.png" alt="RentsGo" style={{ height: 36, objectFit: "contain", marginBottom: 12 }} />
          <div style={{ fontSize: 14, color: "#6B7280" }}>Property management made simple.</div>
        </div>

        {/* Card */}
        <div style={{
          background: "white",
          borderRadius: 20,
          padding: "36px 32px",
          boxShadow: "0 4px 24px rgba(0,0,0,0.08)",
        }}>
          {step === "sent" ? (
            /* Success state */
            <div style={{ textAlign: "center" }}>
              <div style={{ fontSize: 44, marginBottom: 16 }}>{channel === "email" ? "📬" : "📱"}</div>
              <div style={{ fontSize: 22, fontWeight: 800, color: "#1a1a2e", letterSpacing: "-0.01em", marginBottom: 6 }}>
                1-2-3... RentsGo!
              </div>
              <div style={{ fontSize: 14, fontWeight: 400, color: "#6B7280", marginBottom: 8 }}>
                {channel === "email" ? "Check your inbox" : "Check your messages"}
              </div>
              <div style={{ fontSize: 14, color: "#6B7280", lineHeight: 1.6 }}>
                We sent a login link to<br />
                <span style={{ fontWeight: 600, color: "#111827" }}>{channel === "email" ? email : phone}</span>
              </div>
              <div style={{ fontSize: 13, color: "#9CA3AF", marginTop: 20 }}>
                Link expires in 15 minutes.
              </div>
              <button
                onClick={() => { setStep("idle"); setEmail(""); setPhone(""); }}
                style={{
                  marginTop: 24,
                  background: "none",
                  border: "none",
                  color: "#1a1a2e",
                  fontSize: 14,
                  fontWeight: 600,
                  cursor: "pointer",
                  padding: 0,
                  textDecoration: "underline",
                }}
              >
                {channel === "email" ? "Use a different email" : "Use a different number"}
              </button>
            </div>
          ) : (
            /* Email/SMS form */
            <form onSubmit={handleSubmit}>
              <div style={{ fontSize: 20, fontWeight: 700, color: "#111827", marginBottom: 6 }}>Log in</div>
              <div style={{ fontSize: 14, color: "#6B7280", marginBottom: 20 }}>
                {channel === "email"
                  ? "Enter your email and we'll send you a secure login link."
                  : "Enter your phone number and we'll text you a secure login link."}
              </div>

              {/* Channel toggle */}
              <div style={{
                display: "flex", background: "#F3F4F6", borderRadius: 10, padding: 3, marginBottom: 20,
              }}>
                {(["email", "sms"] as Channel[]).map(c => (
                  <button
                    key={c}
                    type="button"
                    onClick={() => { setChannel(c); setError(null); }}
                    style={{
                      flex: 1, padding: "8px 0", fontSize: 13, fontWeight: 600,
                      border: "none", borderRadius: 8, cursor: "pointer",
                      background: channel === c ? "white" : "transparent",
                      color: channel === c ? "#1a1a2e" : "#6B7280",
                      boxShadow: channel === c ? "0 1px 3px rgba(0,0,0,0.1)" : "none",
                      transition: "all 0.15s",
                    }}
                  >
                    {c === "email" ? "Email" : "Text"}
                  </button>
                ))}
              </div>

              <label style={{ display: "block", fontSize: 13, fontWeight: 500, color: "#374151", marginBottom: 6 }}>
                {channel === "email" ? "Email address" : "Phone number"}
              </label>
              {channel === "email" ? (
                <input
                  type="email"
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  autoFocus
                  required
                  style={{
                    width: "100%",
                    padding: "12px 14px",
                    fontSize: 15,
                    border: "1.5px solid #E5E7EB",
                    borderRadius: 10,
                    boxSizing: "border-box",
                    outline: "none",
                    marginBottom: 20,
                    transition: "border-color 0.15s",
                  }}
                  onFocus={e => e.target.style.borderColor = "#1a1a2e"}
                  onBlur={e => e.target.style.borderColor = "#E5E7EB"}
                />
              ) : (
                <PhoneInput
                  value={phone}
                  onChange={setPhone}
                  autoFocus
                  style={{
                    width: "100%",
                    padding: "12px 14px",
                    fontSize: 15,
                    border: "1.5px solid #E5E7EB",
                    borderRadius: 10,
                    boxSizing: "border-box",
                    outline: "none",
                    marginBottom: 20,
                    transition: "border-color 0.15s",
                  }}
                />
              )}

              {error && (
                <div style={{
                  background: "#FEF2F2",
                  color: "#DC2626",
                  fontSize: 13,
                  padding: "10px 14px",
                  borderRadius: 8,
                  marginBottom: 16,
                }}>
                  {error}
                </div>
              )}

              <button
                type="submit"
                disabled={step === "sending" || !canSubmit}
                style={{
                  width: "100%",
                  padding: "13px",
                  background: step === "sending" || !canSubmit ? "#9CA3AF" : "#1a1a2e",
                  color: "white",
                  border: "none",
                  borderRadius: 12,
                  fontSize: 16,
                  fontWeight: 600,
                  cursor: step === "sending" || !canSubmit ? "not-allowed" : "pointer",
                  transition: "background 0.15s",
                }}
              >
                {step === "sending" ? "Sending..." : channel === "email" ? "Send Login Link" : "Text Me a Login Link"}
              </button>
            </form>
          )}
        </div>

        <div style={{ textAlign: "center", marginTop: 24, fontSize: 13, color: "#9CA3AF" }}>
          © {new Date().getFullYear()} RentsGo
        </div>
      </div>
    </div>
  );
}
